import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isValid, parse } from 'date-fns';
import { Appointment } from '@/types';

export const useAppointmentDialog = (
  selectedDate: Date | undefined,
  setSelectedDate: (date: Date | undefined) => void,
  selectedTime: string | undefined,
  setSelectedTime: (time: string | undefined) => void,
  options?: { syncCreateFromUrl?: boolean },
) => {
  const syncCreateFromUrl = options?.syncCreateFromUrl !== false;
  const [searchParams, setSearchParams] = useSearchParams();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [appointmentDetailOpen, setAppointmentDetailOpen] = useState(false);
  const [checkingCalendarStatus, setCheckingCalendarStatus] = useState(false);

  // Update URL when modal state changes
  const updateCreateDialogState = (open: boolean) => {
    setCreateDialogOpen(open);
    const newSearchParams = new URLSearchParams(searchParams);
    if (open) {
      newSearchParams.set('create', 'true');
    } else {
      newSearchParams.delete('create');
      newSearchParams.delete('date');
      newSearchParams.delete('time');
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  // Sync modal state with URL query parameter on mount and when URL changes
  useEffect(() => {
    if (!syncCreateFromUrl) return;

    const createParam = searchParams.get('create');
    if (createParam === 'true' && !createDialogOpen) {
      // Open modal if URL has create=true but modal is closed
      // Only open if not already checking calendar status to avoid duplicate calls
      if (!checkingCalendarStatus) {
        handleOpenCreateDialog();
      }
    } else if (createParam !== 'true' && createDialogOpen) {
      // Close modal if URL doesn't have create=true but modal is open
      // This handles browser back button
      setCreateDialogOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Function to handle opening the create appointment dialog.
  // Calendar integration is managed server-side, no OAuth pre-check needed.
  const handleOpenCreateDialog = async (date?: Date, time?: string) => {
    setCheckingCalendarStatus(true);
    try {
      const dateFromUrl = searchParams.get('date');
      const timeFromUrl = searchParams.get('time');

      let resolvedDate = date;
      if (!resolvedDate && dateFromUrl) {
        const parsed = parse(dateFromUrl, 'yyyy-MM-dd', new Date());
        if (isValid(parsed)) resolvedDate = parsed;
      }
      if (resolvedDate) {
        setSelectedDate(resolvedDate);
      }

      const resolvedTime = time ?? timeFromUrl ?? undefined;
      if (resolvedTime) {
        setSelectedTime(resolvedTime);
      } else if (!selectedTime) {
        const now = new Date();
        now.setHours(now.getHours() + 1, 0, 0, 0);
        const timeString = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        setSelectedTime(timeString);
      }

      setSelectedAppointment(null);
      updateCreateDialogState(true);
    } finally {
      setCheckingCalendarStatus(false);
    }
  };

  return {
    createDialogOpen,
    selectedAppointment,
    appointmentDetailOpen,
    checkingCalendarStatus,
    setCheckingCalendarStatus,
    updateCreateDialogState,
    handleOpenCreateDialog,
    setSelectedAppointment,
    setAppointmentDetailOpen,
  };
};
